import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatMoney } from '@johnsaks/core'
import { useMemo, useState } from 'react'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { DataTable, type DataColumn } from '@/components/data-table'
import { BtnGhost, BtnPrimary, Field, Select, TextInput } from '@/components/field'
import { Modal } from '@/components/modal'
import { RowActions } from '@/components/row-actions'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate, downloadCsv, type OrderRow } from '@/lib/api'
import { centsFromDollars, fmtDateTime, ORDER_STATUS } from '@/lib/labels'

const PAGE_SIZE = 25

const TABS: Array<{ id: string; label: string }> = [
  { id: '', label: 'Toutes' },
  { id: 'PENDING', label: 'En attente' },
  { id: 'TO_PREPARE', label: 'À préparer' },
  { id: 'CONFIRMED', label: 'Confirmées' },
  { id: 'PREPARING', label: 'Préparation' },
  { id: 'SHIPPED', label: 'Expédiées' },
  { id: 'OUT_FOR_DELIVERY', label: 'En livraison' },
  { id: 'DELIVERED', label: 'Livrées' },
  { id: 'CANCELLED', label: 'Annulées' },
]

const PREPARE_STATUSES = new Set(['CONFIRMED', 'PREPARING'])

type CustomerHit = {
  id: string
  email: string
  firstName: string
  lastName: string
  phone: string | null
}

type VariantHit = {
  variantId: string
  sku: string
  label: string
  productName?: string
  available: number
  priceCents?: number
}

type InventoryRow = {
  productId: string
  name: string
  priceCents?: number
  variants: Array<{
    variantId: string
    sku: string
    label: string
    available: number
  }>
}

type CommuneRow = { id: string; name: string }

type LineDraft = { variantId: string; label: string; quantity: number; unitPriceCents: number }

function urlQ() {
  return new URLSearchParams(window.location.search).get('q')?.trim() ?? ''
}

export function Commandes() {
  const toast = useToast()
  const qc = useQueryClient()
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [pending, setPending] = useState<{ ref: string; status: string } | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const searchQ = urlQ()

  const orders = useQuery({
    queryKey: ['admin-orders', status, searchQ, page],
    queryFn: () => {
      const params = new URLSearchParams()
      params.set('limit', String(PAGE_SIZE))
      params.set('page', String(page))
      if (status && status !== 'TO_PREPARE') params.set('status', status)
      if (status === 'TO_PREPARE') {
        // Client filter for multi-status tab; pull a wider page.
        params.set('limit', '100')
        params.delete('page')
      }
      if (searchQ) params.set('q', searchQ)
      return api<{ items: OrderRow[]; total: number; page: number; pageSize: number }>(
        `/admin/orders?${params.toString()}`,
      )
    },
  })

  const changeStatus = useMutation({
    mutationFn: () =>
      apiMutate(`/admin/orders/${pending!.ref}/transitions`, 'POST', {
        to: pending!.status,
      }),
    onSuccess: () => {
      toast.ok('Statut mis à jour')
      setPending(null)
      void qc.invalidateQueries({ queryKey: ['admin-orders'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const raw = orders.data?.items ?? []
  const items =
    status === 'TO_PREPARE' ? raw.filter((o) => PREPARE_STATUSES.has(o.status)) : raw
  const total =
    status === 'TO_PREPARE' ? items.length : (orders.data?.total ?? items.length)

  const columns = useMemo<DataColumn<OrderRow>[]>(
    () => [
      {
        header: 'Réf.',
        accessorKey: 'reference',
        cell: (o) => <span className="font-medium text-encre-admin">{o.reference}</span>,
      },
      {
        header: 'Client',
        id: 'client',
        cell: (o) => (
          <div>
            <div>{o.contactName}</div>
            {o.contactPhone ? (
              <div className="text-xs text-second-admin">{o.contactPhone}</div>
            ) : null}
          </div>
        ),
      },
      {
        header: 'Statut',
        accessorKey: 'status',
        cell: (o) => {
          const st = ORDER_STATUS[o.status] ?? { label: o.status, tone: 'neutral' }
          return <StatusPill label={st.label} tone={st.tone} />
        },
      },
      {
        header: 'Articles',
        accessorKey: 'itemCount',
        cell: (o) => <span className="tabular-nums">{o.itemCount ?? '—'}</span>,
      },
      {
        header: 'Total',
        accessorKey: 'totalCents',
        cell: (o) => <span className="tabular-nums">{formatMoney(o.totalCents)}</span>,
      },
      {
        header: 'Date',
        accessorKey: 'placedAt',
        cell: (o) => <span className="text-second-admin">{fmtDateTime(o.placedAt)}</span>,
      },
      {
        header: '',
        id: 'actions',
        cell: (o) => (
          <div className="text-right" onClick={(e) => e.stopPropagation()}>
            <RowActions
              items={[
                {
                  label: 'Ouvrir',
                  onSelect: () => {
                    window.location.href = `/commandes/${o.reference}`
                  },
                },
                {
                  label: 'Changer le statut',
                  onSelect: () => undefined,
                  children: Object.entries(ORDER_STATUS).map(([key, meta]) => ({
                    label: meta.label,
                    onSelect: () => setPending({ ref: o.reference, status: key }),
                    disabled: key === o.status,
                  })),
                },
                {
                  label: 'Copier la réf',
                  onSelect: () => {
                    void navigator.clipboard.writeText(o.reference).then(
                      () => toast.ok('Référence copiée'),
                      () => toast.err('Impossible de copier'),
                    )
                  },
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [toast],
  )

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id || 'all'}
              type="button"
              onClick={() => {
                setStatus(t.id)
                setPage(1)
              }}
              className={`kicker rounded-pilule px-3 py-1.5 ${
                status === t.id
                  ? 'bg-encre-admin text-creme'
                  : 'border border-bordure-admin text-encre-admin'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <BtnGhost
            onClick={() =>
              void downloadCsv('/admin/exports/orders.csv', 'orders.csv').catch((e: Error) =>
                toast.err(e.message),
              )
            }
          >
            Export CSV
          </BtnGhost>
          <BtnPrimary onClick={() => setCreateOpen(true)}>Nouvelle commande</BtnPrimary>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={items}
        getRowId={(o) => o.reference}
        isLoading={orders.isLoading}
        isError={orders.isError}
        errorMessage="Impossible de charger les commandes."
        emptyTitle="Aucune commande"
        emptyBody="Aucune commande pour ce filtre."
        onRowClick={(o) => {
          window.location.href = `/commandes/${o.reference}`
        }}
        pagination={
          status === 'TO_PREPARE'
            ? undefined
            : {
                page,
                pageSize: PAGE_SIZE,
                total,
                onPageChange: setPage,
              }
        }
      />

      <ConfirmDialog
        open={Boolean(pending)}
        title="Changer le statut ?"
        body={
          pending
            ? `Passer ${pending.ref} en « ${ORDER_STATUS[pending.status]?.label ?? pending.status} ».`
            : ''
        }
        confirmLabel="Confirmer"
        busy={changeStatus.isPending}
        onClose={() => setPending(null)}
        onConfirm={() => changeStatus.mutate()}
      />

      <CreateOrderModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(ref) => {
          setCreateOpen(false)
          void qc.invalidateQueries({ queryKey: ['admin-orders'] })
          window.location.href = `/commandes/${ref}`
        }}
      />
    </div>
  )
}

function CreateOrderModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (ref: string) => void
}) {
  const toast = useToast()
  const [customerQ, setCustomerQ] = useState('')
  const [customer, setCustomer] = useState<CustomerHit | null>(null)
  const [productQ, setProductQ] = useState('')
  const [lines, setLines] = useState<LineDraft[]>([])
  const [mode, setMode] = useState<'HOME' | 'PICKUP_POINT'>('HOME')
  const [communeId, setCommuneId] = useState('')
  const [addressLine, setAddressLine] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('CASH_ON_DELIVERY')
  const [source, setSource] = useState('PHONE')
  const [deliveryDollars, setDeliveryDollars] = useState('0')

  const customers = useQuery({
    queryKey: ['admin-customers-search', customerQ],
    queryFn: () =>
      api<{ items: CustomerHit[] }>(
        `/admin/customers?q=${encodeURIComponent(customerQ)}&limit=8`,
      ),
    enabled: open && customerQ.trim().length >= 2,
  })

  const inventory = useQuery({
    queryKey: ['admin-inventory-search', productQ],
    queryFn: () =>
      api<{ items: InventoryRow[] }>(
        `/admin/inventory?q=${encodeURIComponent(productQ)}&limit=20`,
      ),
    enabled: open && productQ.trim().length >= 2,
  })

  const communes = useQuery({
    queryKey: ['admin-communes'],
    queryFn: () => api<{ items: CommuneRow[] }>('/admin/delivery/communes'),
    enabled: open,
  })

  const variantHits = useMemo(() => {
    const items = inventory.data?.items ?? []
    const hits: VariantHit[] = []
    for (const p of items) {
      for (const v of p.variants ?? []) {
        hits.push({
          variantId: v.variantId,
          sku: v.sku,
          label: `${p.name} · ${v.label || v.sku}`,
          available: v.available,
          priceCents: p.priceCents,
        })
      }
    }
    return hits
  }, [inventory.data])

  const create = useMutation({
    mutationFn: () => {
      if (!customer) throw new Error('Choisissez un client')
      if (lines.length === 0) throw new Error('Ajoutez au moins une ligne')
      const idem =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `staff-${Date.now()}`
      return apiMutate<{ reference: string }>(
        '/admin/orders',
        'POST',
        {
          customerId: customer.id,
          source,
          mode,
          communeId: mode === 'HOME' ? communeId || undefined : undefined,
          addressLine: mode === 'HOME' ? addressLine : undefined,
          contactName: contactName || `${customer.firstName} ${customer.lastName}`.trim(),
          contactPhone: contactPhone || customer.phone || '',
          contactEmail: customer.email,
          paymentMethod,
          deliveryCents: centsFromDollars(deliveryDollars),
          deliveryFeeOverridden: true,
          confirmImmediately: true,
          lines: lines.map((l) => ({
            variantId: l.variantId,
            quantity: l.quantity,
            unitPriceCents: l.unitPriceCents,
          })),
        },
        { 'Idempotency-Key': idem },
      )
    },
    onSuccess: (o) => {
      toast.ok(`Commande ${o.reference} créée`)
      onCreated(o.reference)
    },
    onError: (e: Error) => toast.err(e.message),
  })

  function pickCustomer(c: CustomerHit) {
    setCustomer(c)
    setCustomerQ(`${c.firstName} ${c.lastName}`.trim() || c.email)
    setContactName(`${c.firstName} ${c.lastName}`.trim())
    setContactPhone(c.phone ?? '')
  }

  function addVariant(v: VariantHit) {
    setLines((prev) => {
      const existing = prev.find((l) => l.variantId === v.variantId)
      if (existing) {
        return prev.map((l) =>
          l.variantId === v.variantId ? { ...l, quantity: l.quantity + 1 } : l,
        )
      }
      return [
        ...prev,
        {
          variantId: v.variantId,
          label: v.label,
          quantity: 1,
          unitPriceCents: v.priceCents ?? 0,
        },
      ]
    })
    setProductQ('')
  }

  return (
    <Modal
      open={open}
      title="Nouvelle commande"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <BtnGhost onClick={onClose}>Annuler</BtnGhost>
          <BtnPrimary disabled={create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Création…' : 'Créer'}
          </BtnPrimary>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Client">
          <TextInput
            value={customerQ}
            onChange={(e) => {
              setCustomerQ(e.target.value)
              setCustomer(null)
            }}
            placeholder="Nom, e-mail ou téléphone…"
          />
          {customers.data?.items?.length ? (
            <ul className="mt-1 max-h-36 overflow-auto rounded-champ border border-bordure-admin">
              {customers.data.items.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gris-fond/60"
                    onClick={() => pickCustomer(c)}
                  >
                    {c.firstName} {c.lastName} · {c.email}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {customer ? (
            <p className="text-xs text-second-admin">Sélection : {customer.email}</p>
          ) : null}
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Source">
            <Select value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="PHONE">Téléphone</option>
              <option value="WHATSAPP">WhatsApp</option>
              <option value="SHOWROOM">Showroom</option>
              <option value="OTHER">Autre</option>
            </Select>
          </Field>
          <Field label="Paiement">
            <Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              <option value="CASH_ON_DELIVERY">COD</option>
              <option value="BANK_TRANSFER">Virement</option>
              <option value="ORANGE_MONEY">Orange Money</option>
              <option value="MPESA">M-Pesa</option>
              <option value="AIRTEL_MONEY">Airtel Money</option>
            </Select>
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Contact">
            <TextInput value={contactName} onChange={(e) => setContactName(e.target.value)} />
          </Field>
          <Field label="Téléphone">
            <TextInput value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Mode">
            <Select
              value={mode}
              onChange={(e) => setMode(e.target.value as 'HOME' | 'PICKUP_POINT')}
            >
              <option value="HOME">Domicile</option>
              <option value="PICKUP_POINT">Point relais</option>
            </Select>
          </Field>
          <Field label="Frais livraison ($)">
            <TextInput
              value={deliveryDollars}
              onChange={(e) => setDeliveryDollars(e.target.value)}
            />
          </Field>
        </div>

        {mode === 'HOME' ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Commune">
              <Select value={communeId} onChange={(e) => setCommuneId(e.target.value)}>
                <option value="">—</option>
                {(communes.data?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Adresse">
              <TextInput value={addressLine} onChange={(e) => setAddressLine(e.target.value)} />
            </Field>
          </div>
        ) : null}

        <Field label="Ajouter un produit">
          <TextInput
            value={productQ}
            onChange={(e) => setProductQ(e.target.value)}
            placeholder="SKU ou nom…"
          />
          {variantHits.length ? (
            <ul className="mt-1 max-h-36 overflow-auto rounded-champ border border-bordure-admin">
              {variantHits.slice(0, 12).map((v) => (
                <li key={v.variantId}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gris-fond/60"
                    onClick={() => addVariant(v)}
                  >
                    <span>{v.label}</span>
                    <span className="text-xs text-second-admin">
                      stock {v.available}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </Field>

        {lines.length ? (
          <ul className="space-y-2 text-sm">
            {lines.map((l) => (
              <li
                key={l.variantId}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-bordure-admin pb-2"
              >
                <span>{l.label}</span>
                <span className="flex items-center gap-2">
                  <TextInput
                    type="number"
                    min={1}
                    className="w-16"
                    value={l.quantity}
                    onChange={(e) =>
                      setLines((prev) =>
                        prev.map((x) =>
                          x.variantId === l.variantId
                            ? { ...x, quantity: Math.max(1, Number(e.target.value) || 1) }
                            : x,
                        ),
                      )
                    }
                  />
                  <span className="tabular-nums">{formatMoney(l.unitPriceCents * l.quantity)}</span>
                  <BtnGhost
                    onClick={() =>
                      setLines((prev) => prev.filter((x) => x.variantId !== l.variantId))
                    }
                  >
                    Retirer
                  </BtnGhost>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-second-admin">Aucune ligne pour l’instant.</p>
        )}
      </div>
    </Modal>
  )
}
